import { ReactNode, useMemo } from 'react';
import { LocalizationProvider as NucleusLocalizationProvider } from 'nucleus-text';
import useMeQuery from '@hooks/useMeQuery';
import locales, { translate, resolveLocales, defaultLocale } from '../../../locales';

interface Props {
  children: ReactNode;
  locale?: string;
}

function LocalizationProvider({ children, locale }: Props): JSX.Element {
  // Use resourceOverrides from ME query
  const { data } = useMeQuery();
  const overrides = useMemo<Record<string, string>>(
    () =>
      data?.me?.event?.resourceOverrides?.overrides?.reduce((acc, { key, value = '' }) => {
        acc[key] = value.replace(/\\/g, '\\\\');
        return acc;
      }, {}) || {},
    [data]
  );

  // Combine overrides with locales promise map
  const localesWithOverrides = useMemo(() => {
    const overridesWithDefault = {
      ...overrides,
      default: overrides
    };
    return Object.entries(locales).reduce((acc, [localeKey, localePromise]) => {
      acc[localeKey] = resolveLocales(() => [localePromise(), overridesWithDefault]);
      return acc;
    }, {});
  }, [overrides]);

  return (
    <NucleusLocalizationProvider
      locale={locale}
      locales={localesWithOverrides}
      translate={translate}
      defaultLocale={defaultLocale}
    >
      {children}
    </NucleusLocalizationProvider>
  );
}

export default LocalizationProvider;
